import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SEO } from "@/components/SEO";
import ArqNavigation from "@/components/arqera/ArqNavigation";
import ArqFooter from "@/components/arqera/ArqFooter";

type Maturity = "planning" | "piloting" | "production" | "mature";
type Surface = "llm_apps" | "agentic" | "internal_apps" | "apis" | "none";
type Industry =
  | "healthcare"
  | "financial_services"
  | "legal"
  | "saas"
  | "manufacturing"
  | "public_sector"
  | "other";
type Pressure =
  | "customer_security_review"
  | "regulatory_audit"
  | "board_governance"
  | "incident"
  | "proactive";
type Timeline = "urgent_30d" | "30_90d" | "90d_plus" | "exploring";

interface IntakeState {
  name: string;
  email: string;
  company: string;
  jobTitle: string;
  aiMaturity: Maturity | "";
  surfaces: Surface[];
  industry: Industry | "";
  pressure: Pressure | "";
  timeline: Timeline | "";
  hasSecurityTeam: "yes" | "no" | "";
  note: string;
  consentGiven: boolean;
  honeypot: string;
}

interface RoutingResult {
  tier: string;
  recommendation: string;
  score: number;
}

const maturityOptions: { value: Maturity; label: string; sub: string }[] = [
  { value: "planning", label: "Planning", sub: "Evaluating AI initiatives" },
  { value: "piloting", label: "Piloting", sub: "Limited internal pilots" },
  { value: "production", label: "In production", sub: "AI in live workflows" },
  { value: "mature", label: "Mature", sub: "Multiple production systems" },
];

const surfaceOptions: { value: Surface; label: string }[] = [
  { value: "llm_apps", label: "LLM applications" },
  { value: "agentic", label: "Agentic workflows" },
  { value: "internal_apps", label: "Internal applications" },
  { value: "apis", label: "APIs" },
  { value: "none", label: "None deployed yet" },
];

const industryOptions: { value: Industry; label: string }[] = [
  { value: "healthcare", label: "Healthcare" },
  { value: "financial_services", label: "Financial Services" },
  { value: "legal", label: "Legal" },
  { value: "saas", label: "SaaS / Technology" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "public_sector", label: "Public Sector" },
  { value: "other", label: "Other" },
];

const pressureOptions: { value: Pressure; label: string }[] = [
  { value: "customer_security_review", label: "Customer security review" },
  { value: "regulatory_audit", label: "Regulatory audit (SOC 2, HIPAA, ISO)" },
  { value: "board_governance", label: "Board or governance milestone" },
  { value: "incident", label: "Incident or near-miss" },
  { value: "proactive", label: "Proactive posture work" },
];

const timelineOptions: { value: Timeline; label: string }[] = [
  { value: "urgent_30d", label: "Within 30 days" },
  { value: "30_90d", label: "30 to 90 days" },
  { value: "90d_plus", label: "90+ days" },
  { value: "exploring", label: "Exploring" },
];

const initial: IntakeState = {
  name: "",
  email: "",
  company: "",
  jobTitle: "",
  aiMaturity: "",
  surfaces: [],
  industry: "",
  pressure: "",
  timeline: "",
  hasSecurityTeam: "",
  note: "",
  consentGiven: false,
  honeypot: "",
};

const inputCls =
  "w-full bg-arq-surface1 border border-arq-border text-arq-text placeholder:text-arq-tertiary rounded-md px-4 py-[14px] text-[15px] focus:border-arq-blue focus:outline-none focus:ring-2 focus:ring-arq-blue/20 transition-colors";

const labelCls = "block text-[13px] text-arq-muted mb-2 font-medium";

const sectionLabelCls =
  "font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-blue mb-3";

export default function SecurityIntakePage() {
  const { toast } = useToast();
  const [form, setForm] = useState<IntakeState>(initial);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RoutingResult | null>(null);

  const set = <K extends keyof IntakeState>(k: K, v: IntakeState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggleSurface = (s: Surface) => {
    setForm((f) => {
      const has = f.surfaces.includes(s);
      let next: Surface[];
      if (s === "none") {
        next = has ? [] : ["none"];
      } else {
        const without = f.surfaces.filter((x) => x !== "none");
        next = has ? without.filter((x) => x !== s) : [...without, s];
      }
      return { ...f, surfaces: next };
    });
  };

  const isValid = useMemo(() => {
    return (
      form.name.trim().length > 1 &&
      /.+@.+\..+/.test(form.email) &&
      form.company.trim().length > 0 &&
      form.aiMaturity &&
      form.surfaces.length > 0 &&
      form.industry &&
      form.pressure &&
      form.timeline &&
      form.hasSecurityTeam &&
      form.consentGiven
    );
  }, [form]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || loading) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("security-intake", {
        body: {
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          jobTitle: form.jobTitle.trim() || null,
          aiMaturity: form.aiMaturity,
          surfaces: form.surfaces,
          industry: form.industry,
          pressure: form.pressure,
          timeline: form.timeline,
          hasSecurityTeam: form.hasSecurityTeam === "yes",
          note: form.note.trim() || null,
          consentGiven: form.consentGiven,
          honeypot: form.honeypot,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setResult({
        tier: data.tier,
        recommendation: data.recommendation,
        score: data.score,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      toast({
        title: "Submission failed",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-arq-bg text-arq-text font-body min-h-screen">
      <SEO
        title="AI Security Intake | ArqeraAI"
        description="A short qualification intake for the AI Feature Security Review. Get routed to the right engagement and a written proposal within 48 hours."
        canonical="https://arqeraai.com/security-intake"
      />
      <ArqNavigation />

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-[820px] mx-auto">
          {result ? (
            <ResultView result={result} name={form.name} />
          ) : (
            <>
              <header className="mb-12 text-center">
                <div className="arq-label mb-6 inline-flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" aria-hidden />
                  AI Security Intake
                </div>
                <h1
                  className="font-display font-semibold text-arq-text mb-5"
                  style={{ fontSize: "clamp(28px, 4vw, 44px)", lineHeight: 1.15 }}
                >
                  Tell us about your AI footprint.
                </h1>
                <p className="text-arq-muted text-[17px] leading-[1.7] max-w-[640px] mx-auto">
                  Six questions. About two minutes. We use your answers to route you to the
                  right engagement shape and respond with a written proposal within 48 hours.
                </p>
              </header>

              <form onSubmit={onSubmit} className="arq-card p-7 md:p-10 space-y-10">
                {/* Section 1: Contact */}
                <section aria-labelledby="sec-contact">
                  <div className={sectionLabelCls} id="sec-contact">01 · Contact</div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls} htmlFor="name">Name</label>
                      <input
                        id="name"
                        required
                        className={inputCls}
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="email">Work email</label>
                      <input
                        id="email"
                        type="email"
                        required
                        className={inputCls}
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="company">Company</label>
                      <input
                        id="company"
                        required
                        className={inputCls}
                        value={form.company}
                        onChange={(e) => set("company", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="title">
                        Title <span className="text-arq-tertiary">(optional)</span>
                      </label>
                      <input
                        id="title"
                        className={inputCls}
                        value={form.jobTitle}
                        onChange={(e) => set("jobTitle", e.target.value)}
                      />
                    </div>
                  </div>
                </section>

                {/* Section 2: AI footprint */}
                <section aria-labelledby="sec-ai">
                  <div className={sectionLabelCls} id="sec-ai">02 · AI footprint</div>
                  <div className="mb-6">
                    <span className={labelCls}>Where is AI in your organization today?</span>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {maturityOptions.map((opt) => {
                        const selected = form.aiMaturity === opt.value;
                        return (
                          <button
                            type="button"
                            key={opt.value}
                            onClick={() => set("aiMaturity", opt.value)}
                            className={`text-left px-4 py-3 rounded-md border transition-colors ${
                              selected
                                ? "border-arq-blue bg-arq-blue/10 text-arq-text"
                                : "border-arq-border bg-arq-surface1 text-arq-text hover:border-arq-blue/60"
                            }`}
                            aria-pressed={selected}
                          >
                            <div className="text-[14px] font-semibold">{opt.label}</div>
                            <div className="text-[12px] text-arq-muted mt-0.5">{opt.sub}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <span className={labelCls}>
                      Which surfaces are in scope? <span className="text-arq-tertiary">(select all that apply)</span>
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {surfaceOptions.map((opt) => {
                        const selected = form.surfaces.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggleSurface(opt.value)}
                            className={`px-4 py-2 rounded-full border text-[13px] transition-colors ${
                              selected
                                ? "border-arq-blue bg-arq-blue/15 text-arq-text"
                                : "border-arq-border bg-arq-surface1 text-arq-muted hover:text-arq-text hover:border-arq-blue/60"
                            }`}
                            aria-pressed={selected}
                          >
                            {selected && <Check className="inline w-3.5 h-3.5 mr-1.5" aria-hidden />}
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>

                {/* Section 3: Context */}
                <section aria-labelledby="sec-context">
                  <div className={sectionLabelCls} id="sec-context">03 · Context</div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls} htmlFor="industry">Industry</label>
                      <select
                        id="industry"
                        required
                        className={inputCls}
                        value={form.industry}
                        onChange={(e) => set("industry", e.target.value as Industry)}
                      >
                        <option value="">Select…</option>
                        {industryOptions.map((i) => (
                          <option key={i.value} value={i.value}>{i.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="timeline">Timeline</label>
                      <select
                        id="timeline"
                        required
                        className={inputCls}
                        value={form.timeline}
                        onChange={(e) => set("timeline", e.target.value as Timeline)}
                      >
                        <option value="">Select…</option>
                        {timelineOptions.map((i) => (
                          <option key={i.value} value={i.value}>{i.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-5">
                    <span className={labelCls}>What is driving this?</span>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {pressureOptions.map((opt) => {
                        const selected = form.pressure === opt.value;
                        return (
                          <button
                            type="button"
                            key={opt.value}
                            onClick={() => set("pressure", opt.value)}
                            className={`text-left px-4 py-3 rounded-md border text-[14px] transition-colors ${
                              selected
                                ? "border-arq-blue bg-arq-blue/10 text-arq-text"
                                : "border-arq-border bg-arq-surface1 text-arq-text hover:border-arq-blue/60"
                            }`}
                            aria-pressed={selected}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-5">
                    <span className={labelCls}>Do you have an in-house security team?</span>
                    <div className="flex gap-3">
                      {(["yes", "no"] as const).map((v) => {
                        const selected = form.hasSecurityTeam === v;
                        return (
                          <button
                            type="button"
                            key={v}
                            onClick={() => set("hasSecurityTeam", v)}
                            className={`px-6 py-2.5 rounded-md border text-[14px] capitalize transition-colors ${
                              selected
                                ? "border-arq-blue bg-arq-blue/10 text-arq-text"
                                : "border-arq-border bg-arq-surface1 text-arq-muted hover:text-arq-text hover:border-arq-blue/60"
                            }`}
                            aria-pressed={selected}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>

                {/* Section 4: Note */}
                <section aria-labelledby="sec-note">
                  <div className={sectionLabelCls} id="sec-note">04 · Anything else</div>
                  <label className={labelCls} htmlFor="note">
                    Brief note <span className="text-arq-tertiary">(optional)</span>
                  </label>
                  <textarea
                    id="note"
                    rows={4}
                    className={inputCls}
                    value={form.note}
                    onChange={(e) => set("note", e.target.value)}
                    placeholder="Specific systems, audit deadlines, or constraints we should know about."
                  />
                </section>

                {/* Consent */}
                <label className="flex items-start gap-3 text-[13px] text-arq-muted">
                  <input
                    type="checkbox"
                    checked={form.consentGiven}
                    onChange={(e) => set("consentGiven", e.target.checked)}
                    className="mt-1 w-4 h-4 accent-arq-blue"
                    required
                  />
                  <span>
                    I agree to ArqeraAI processing this intake to scope a potential engagement.
                    See our <Link to="/privacy" className="text-arq-blue hover:underline">privacy notice</Link>.
                  </span>
                </label>

                {/* Honeypot */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.honeypot}
                  onChange={(e) => set("honeypot", e.target.value)}
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
                  aria-hidden="true"
                />

                <button
                  type="submit"
                  disabled={!isValid || loading}
                  className="w-full bg-arq-blue text-white font-semibold text-[16px] py-[18px] rounded-md hover:bg-arq-blue-bright hover:shadow-[0_0_24px_rgba(26,111,212,0.35)] transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> Routing your intake…
                    </>
                  ) : (
                    <>
                      Submit intake <ArrowRight className="w-4 h-4" aria-hidden />
                    </>
                  )}
                </button>

                <p className="text-center text-[12px] text-arq-tertiary">
                  No sales pitch. Written proposal within 48 hours.
                </p>
              </form>
            </>
          )}
        </div>
      </main>

      <ArqFooter />
    </div>
  );
}

function ResultView({ result, name }: { result: RoutingResult; name: string }) {
  const firstName = name.split(" ")[0] || "there";
  return (
    <div className="arq-card p-8 md:p-12 text-center">
      <div className="arq-label mb-6 inline-flex items-center gap-2 justify-center">
        <ShieldCheck className="w-4 h-4" aria-hidden /> Intake received
      </div>
      <h1
        className="font-display font-semibold text-arq-text mb-4"
        style={{ fontSize: "clamp(26px, 3.6vw, 38px)", lineHeight: 1.2 }}
      >
        Thank you, {firstName}.
      </h1>
      <p className="text-arq-muted text-[16px] leading-[1.7] max-w-[560px] mx-auto mb-8">
        Based on your responses, the recommended engagement shape is:
      </p>

      <div className="border border-arq-blue/50 bg-arq-blue/10 rounded-lg p-6 max-w-[560px] mx-auto mb-8 text-left">
        <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-blue mb-2">
          Recommended engagement
        </div>
        <div className="font-display text-[22px] font-semibold text-arq-text mb-3">
          {result.tier}
        </div>
        <p className="text-arq-text/90 text-[15px] leading-[1.65]">{result.recommendation}</p>
      </div>

      <p className="text-arq-muted text-[15px] leading-[1.7] max-w-[560px] mx-auto mb-8">
        A senior engineer will review your intake and respond with a written proposal within 48
        hours. If you would like to move faster, you can book a 45-minute discovery call now.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href="https://calendly.com/hello-arqeraai/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-arq-blue text-white font-semibold text-[15px] px-6 py-3 rounded-md hover:bg-arq-blue-bright transition-colors"
        >
          Book a discovery call
        </a>
        <Link
          to="/"
          className="border border-arq-border text-arq-text font-semibold text-[15px] px-6 py-3 rounded-md hover:border-arq-blue hover:text-arq-blue transition-colors"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}
